import {useCallback, useState} from 'react'
import {useWindowDimensions, View} from 'react-native'
import * as Clipboard from 'expo-clipboard'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {sanitizeHandle} from '#/lib/strings/handles'
import {
  canApproveJoinRequest,
  type Classroom,
  classroomInvitationLink,
  type JoinRequest,
  parentsMayJoin,
  useClassroomActor,
} from '#/state/classrooms'
import {
  type ClassroomViewer,
  useClassroomRequestsQuery,
  useRegenerateCodeMutation,
  useRespondToRequestMutation,
} from '#/state/queries/classrooms'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import {SquareBehindSquare_Stroke2_Corner2_Rounded as CopyIcon} from '#/components/icons/SquareBehindSquare4'
import {TimesLarge_Stroke2_Corner0_Rounded as XIcon} from '#/components/icons/Times'
import {QrCodeInner} from '#/components/StarterPack/QrCode'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {noRequestMessage, showClassroomError} from './showClassroomError'

/**
 * The school's invitation: the link (and its QR code) and, when the room has
 * one, the code to read out.
 *
 * The link is `classroomInvitationLink`, so it carries the code whenever there
 * is one - a person who opens it does not have to type anything. A room with
 * no code shares its plain address, and its door is the ask.
 */
export function InviteDialog({
  control,
  classroom,
}: {
  control: DialogControlProps
  classroom: Classroom
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const {width} = useWindowDimensions()
  const regenerate = useRegenerateCodeMutation(classroom.id)
  /*
   * The code is drawn small enough that the dialog fits a phone screen, and the
   * bigger one is a *reading* size: four fifths of the screen, capped at 320 so
   * that a tablet does not draw a code taller than the phone reading it.
   */
  const [big, setBig] = useState(false)
  const bigSize = Math.max(160, Math.min(320, Math.floor(width * 0.8)))
  const isPrivate = classroom.visibility === 'private'
  const code = classroom.joinCode
  const link = classroomInvitationLink(classroom)

  const copyCode = useCallback(() => {
    if (!code) return
    void Clipboard.setStringAsync(code)
    Toast.show(l`Invitation code copied`)
  }, [code, l])

  /* A leaked code is rotated here; every link and QR with the old one stops. */
  const newCode = () => {
    regenerate.mutate(undefined, {
      onSuccess: () =>
        Toast.show(l`New code made. The old code no longer works.`),
      onError: error => showClassroomError(i18n, error),
    })
  }

  const copyLink = useCallback(() => {
    void Clipboard.setStringAsync(link)
    Toast.show(l`Classroom link copied`)
  }, [link, l])

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Invite people to ${classroom.name}`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>
            {l`Invite to ${classroom.name}`}
          </Text>
          {/*
            A student brings a parent's seat with them — *where the room takes
            parents*. Saying it in a classroom that keeps parents out would be a
            promise the room's own door then refuses, so the rule decides the
            sentence, not the invitation.

            Short, because this dialog is the invitation *and* the thing it
            invites with: the code, the square and the button all have to fit a
            phone screen together, and a paragraph of explanation is what pushed
            the button that copies the link below the fold.
          */}
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {/*
              The sentence names what the school is actually handing over. For a
              private room that is the invitation link (the code rides on it); for
              a public one it is the room's own address, and the door there is the
              ask — saying *code* to a room that has none, and whose page offers
              none, is the invitation promising a door the product denies.
            */}
            {isPrivate
              ? l`This classroom is private: only its members read its work. Anyone with the invitation link can ask to join.`
              : l`Anyone with this link can ask to join. You decide who comes in.`}{' '}
            {parentsMayJoin(classroom)
              ? l`A student who joins brings their parent with them.`
              : l`This classroom does not accept parents.`}
          </Text>

          {/*
            The code, for the rooms that have one. A public classroom has none —
            minting one made a door its own page does not offer (`createClassroom`)
            — so the block is not drawn rather than drawn empty, and the room's
            address above is the whole of what a school hands out.
          */}
          {code ? (
            <View
              style={[
                a.align_center,
                a.gap_sm,
                a.py_md,
                a.rounded_md,
                t.atoms.bg_contrast_25,
              ]}>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {l`Invitation code`}
              </Text>
              <Text
                testID="classroomJoinCode"
                selectable
                style={[a.text_3xl, a.font_bold, {letterSpacing: 6}]}>
                {code}
              </Text>
              <Button
                testID="classroomCopyCode"
                label={l`Copy code`}
                color="secondary"
                size="small"
                onPress={copyCode}>
                <ButtonIcon icon={CopyIcon} />
                <ButtonText>{l`Copy code`}</ButtonText>
              </Button>
              <Button
                testID="classroomNewCode"
                label={l`Make a new invitation code`}
                color="secondary"
                size="small"
                disabled={regenerate.isPending}
                onPress={newCode}>
                <ButtonText>{l`New code`}</ButtonText>
              </Button>
              <Text
                style={[
                  a.px_md,
                  a.text_xs,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                {l`A new code stops the old one, and the links and QR codes that carry it.`}
              </Text>
            </View>
          ) : null}

          {/*
            No centre mark, no rounded dots, no brand colour on this one: the
            card makes for a dense pattern, and a dense pattern with a hole
            punched in it — or a soft edge on every module — is a QR a camera
            has to work at. The link is the point here, not the brand.
          */}
          <View style={[a.align_center, a.gap_sm]}>
            <QrCodeInner plain link={link} size={big ? bigSize : 160} />
            <Text
              testID="classroomQrCaption"
              style={[a.text_sm, t.atoms.text_contrast_medium, a.text_center]}>
              {l`Point a phone camera at this to open the classroom`}
            </Text>
            <Button
              testID="classroomQrBigger"
              label={big ? l`Show a smaller code` : l`Show a bigger code`}
              color="secondary"
              size="small"
              onPress={() => setBig(value => !value)}>
              <ButtonText>
                {big ? l`Show a smaller code` : l`Show a bigger code`}
              </ButtonText>
            </Button>
          </View>

          {/*
            The link, copyable: it is the same thing the QR draws, for a school
            that is sending the invitation rather than showing it.
          */}
          <Button
            testID="classroomCopyLink"
            label={l`Copy classroom link`}
            color="secondary"
            size="large"
            onPress={copyLink}>
            <ButtonIcon icon={CopyIcon} />
            <ButtonText>{l`Copy classroom link`}</ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * The asks waiting on the room, read from the server.
 *
 * Who may answer an ask is the room's rule (`canApproveJoinRequest`): the
 * school and its moderators always, and the teachers the room hands that seat
 * to. The arrows are drawn only for the asks this person may answer; the server
 * checks again.
 */
export function RequestsDialog({
  control,
  classroom,
  viewer,
}: {
  control: DialogControlProps
  classroom: Classroom
  viewer: ClassroomViewer
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const actor = useClassroomActor()
  const {data: requests = []} = useClassroomRequestsQuery(
    classroom.id,
    viewer.canManage || viewer.role === 'teacher',
  )

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Requests to join ${classroom.name}`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>{l`Requests to join`}</Text>
          {requests.length === 0 ? (
            <Text
              testID="classroomNoRequests"
              style={[a.text_md, t.atoms.text_contrast_medium]}>
              {l`Nobody has asked to join yet.`}
            </Text>
          ) : (
            <View testID="classroomRequests" style={[a.gap_lg]}>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {plural(requests.length, {
                  one: '# request',
                  other: '# requests',
                })}
              </Text>
              {requests.map(request => (
                <RequestRow
                  key={request.did}
                  classroomId={classroom.id}
                  request={request}
                  canDecide={
                    viewer.canManage ||
                    canApproveJoinRequest(classroom, actor, request.role)
                  }
                />
              ))}
            </View>
          )}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

function RequestRow({
  classroomId,
  request,
  canDecide,
}: {
  classroomId: string
  request: JoinRequest
  canDecide: boolean
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  // One mutation per row, so only this row's buttons wait on its answer
  const respond = useRespondToRequestMutation(classroomId)
  const name = request.displayName || `@${request.handle}`
  const roleLabel =
    request.role === 'teacher'
      ? l`Teacher`
      : request.role === 'student'
        ? l`Student`
        : l`Parent`

  const onDecide = (approve: boolean) => {
    if (respond.isPending) return
    respond.mutate(
      {did: request.did, approve},
      {
        onSuccess: ({alreadyHandled}) => {
          // Somebody else answered first: say so instead of nothing
          if (alreadyHandled) Toast.show(noRequestMessage(i18n))
        },
        onError: error => showClassroomError(i18n, error),
      },
    )
  }

  return (
    <View style={[a.gap_sm]}>
      <Text emoji style={[a.text_md, a.font_semi_bold]}>
        {name}
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {`  ${sanitizeHandle(request.handle, '@')} · ${roleLabel}`}
        </Text>
      </Text>
      {request.message ? (
        <Text emoji style={[a.text_sm, t.atoms.text_contrast_high]}>
          {request.message}
        </Text>
      ) : null}
      {canDecide ? (
        <View style={[a.flex_row, a.gap_sm]}>
          <Button
            testID={`classroomApprove-${request.did}`}
            label={l`Accept ${name}`}
            color="primary"
            size="small"
            disabled={respond.isPending}
            onPress={() => onDecide(true)}>
            <ButtonIcon icon={CheckIcon} />
            <ButtonText>{l`Accept`}</ButtonText>
          </Button>
          <Button
            testID={`classroomDecline-${request.did}`}
            label={l`Decline ${name}`}
            color="secondary"
            size="small"
            disabled={respond.isPending}
            onPress={() => onDecide(false)}>
            <ButtonIcon icon={XIcon} />
            <ButtonText>{l`Decline`}</ButtonText>
          </Button>
        </View>
      ) : (
        <Text
          testID={`classroomRequestNotYours-${request.did}`}
          style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {l`This one is somebody else's to answer.`}
        </Text>
      )}
    </View>
  )
}
